import { pLimit } from "./limit.js";

/** One limiter for ALL preview downloads (feed + compose): they share the
 *  same GramJS media-DC sender, so a per-caller limit wouldn't bound the
 *  real contention. 4 keeps each download fast enough to beat the deadline
 *  on slow links instead of all timing out together. */
export const mediaDownloadLimit = pLimit(4);

/** Deadline wrapper around GramJS downloadMedia. GramJS's media-DC exported
 *  senders can reconnect-loop forever ("Not connected" spam from
 *  Connection.send) and the returned promise then never settles — the race
 *  caps how long the UI waits. The underlying attempt cannot be aborted;
 *  settling the caller so it can show a failure state is the point. */
export const DOWNLOAD_TIMEOUT_MS = 30_000;

/** The timeout rejection's exact message. Exported because groups.ts gates its
 *  media-DC sender reset on "was this a timeout?" — one constant keeps the
 *  throw site and that gate from drifting apart. */
export const DOWNLOAD_TIMEOUT_MESSAGE = "media download timed out";

export function downloadWithTimeout(
  client: { downloadMedia(media: any, opts: any): Promise<any> },
  media: any,
  opts: any,
  timeoutMs: number = DOWNLOAD_TIMEOUT_MS,
  onLate?: (buf: any) => void
): Promise<any> {
  return new Promise((resolve, reject) => {
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      reject(new Error(DOWNLOAD_TIMEOUT_MESSAGE));
    }, timeoutMs);
    client.downloadMedia(media, opts).then(
      (v) => {
        clearTimeout(timer);
        // The transfer can't be aborted; if it outlives the deadline the
        // bytes still arrive — hand them to the caller's cache instead of
        // discarding them so the retry doesn't restart from byte 0.
        if (timedOut) {
          if (v) onLate?.(v);
          return;
        }
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        if (!timedOut) reject(e);
      }
    );
  });
}

/** Drop GramJS's cached media-DC senders so the next download builds a fresh
 *  one. When a sender dies (iOS killing the socket on background/lock),
 *  _connectSender can loop forever and the promise cached in
 *  _exportedSenderPromises never settles — every borrow, including every
 *  retry, awaits it, and only a page reload recovered. Also clears the
 *  release timers: their callbacks re-borrow the OLD sender object, which
 *  would re-poison the cache we just cleared. Reaches into GramJS internals
 *  (telegram 2.26.x: telegramBaseClient._borrowExportedSender) — if these
 *  private maps are absent after an upgrade this degrades to a no-op and the
 *  old reload-to-recover behavior returns. */
export function resetMediaSenders(client: any): void {
  const timeouts: Map<number, any> | undefined = client._exportedSenderReleaseTimeouts;
  if (timeouts) {
    for (const t of timeouts.values()) clearTimeout(t);
    timeouts.clear();
  }
  const promises: Map<number, Promise<any>> | undefined = client._exportedSenderPromises;
  if (!promises) return;
  for (const [dcId, p] of [...promises.entries()]) {
    promises.delete(dcId);
    // Disconnect senders that DID connect (frees the socket); a hung connect
    // promise never settles, so its .then never fires — it's simply orphaned.
    void Promise.resolve(p)
      .then((s) => s?.disconnect?.())
      .catch(() => {});
  }
}

/** Preview target for feed/compose thumbnails. Click-through fetches full
 *  size; the feed never needs more than ~800px. */
const PREVIEW_TARGET_PX = 800;

/** The smallest discrete PhotoSize that is >= PREVIEW_TARGET_PX on its long
 *  edge, for downloadMedia's `thumb` option; largest available when all are
 *  smaller. undefined when the photo has no discrete size ladder (e.g.
 *  PhotoSizeProgressive only) — caller falls back to the default full-size
 *  download.
 *
 *  Returns the PhotoSize OBJECT, not its type string. GramJS's getThumb takes
 *  a string down a match path that returns undefined on a miss — and a missed
 *  thumb downloads zero bytes, surfacing as a hard "couldn't load" — while its
 *  instance branch accepts a PhotoSize and uses it as-is. Passing the object
 *  we already matched skips the re-match entirely. */
export function photoPreviewSize(media: any): any | undefined {
  const sizes: any[] = media?.photo?.sizes || [];
  const real = sizes.filter((s) => s?.className === "PhotoSize" && s.w && s.h);
  if (!real.length) return undefined;
  real.sort((a, b) => Math.max(a.w, a.h) - Math.max(b.w, b.h));
  const fit = real.find((s) => Math.max(s.w, s.h) >= PREVIEW_TARGET_PX);
  return fit ?? real[real.length - 1];
}
